import React from 'react';
import { useTheme } from 'styled-components';

import { ALTURA_PLOT, ALTURA_ROTULO_X, EixoYContainer, ItemPainel, MARGEM_TOPO, Marcador, Painel } from './styles';
import { Formato, Serie } from '../../types/Dashboard';
import { formatarCompacto, formatarValor } from '../../utils/formatadores';

export function criarEscalaY(ticks: number[]) {
  const min = ticks[0];
  const max = ticks[ticks.length - 1];
  return (v: number) => MARGEM_TOPO + ((max - v) / (max - min || 1)) * ALTURA_PLOT;
}

export const ALTURA_SVG = MARGEM_TOPO + ALTURA_PLOT + ALTURA_ROTULO_X;

// Linhas de grade recessivas; a do zero fica mais forte
export function Grade({ ticks, y, largura }: { ticks: number[]; y: (v: number) => number; largura: number }) {
  const theme = useTheme();
  return (
    <>
      {ticks.map(t => (
        <line
          key={t}
          x1={0}
          x2={largura}
          y1={y(t)}
          y2={y(t)}
          stroke={t === 0 ? theme.cores.inkMuted : theme.cores.grade}
          strokeWidth={1}
          shapeRendering='crispEdges'
        />
      ))}
    </>
  );
}

// Rótulos do eixo Y, fora da área rolável
export function EixoY({ ticks, y, formato }: { ticks: number[]; y: (v: number) => number; formato: Formato }) {
  return (
    <EixoYContainer aria-hidden>
      {ticks.map(t => (
        <span key={t} style={{ top: y(t) - 8 }}>{formatarCompacto(t, formato)}</span>
      ))}
    </EixoYContainer>
  );
}

// Rótulo do eixo X; perto das bordas ancora para dentro, senão o texto é cortado
export function RotuloX({ x, texto, largura }: { x: number; texto: string; largura: number }) {
  const theme = useTheme();
  const ancora = x < 24 ? 'start' : x > largura - 24 ? 'end' : 'middle';
  return (
    <text
      x={ancora === 'start' ? Math.max(x - 6, 0) : ancora === 'end' ? Math.min(x + 6, largura) : x}
      y={MARGEM_TOPO + ALTURA_PLOT + 15}
      fontSize={11}
      fill={theme.cores.inkMuted}
      textAnchor={ancora}
    >
      {texto}
    </text>
  );
}

// Valores do ponto sob o mouse (ou tocado), no lugar de um tooltip flutuante
export function PainelSelecao({ series, indice, formato }: { series: Serie[]; indice: number | null; formato: Formato }) {
  const theme = useTheme();

  if (indice === null) {
    return (
      <Painel>
        <span className='dica'>Passe o mouse ou toque no gráfico para ver os valores</span>
      </Painel>
    );
  }

  return (
    <Painel aria-live='polite'>
      <strong>{series[0]?.pontos[indice]?.rotulo}</strong>
      {series.map((s, i) => (
        <ItemPainel key={s.nome}>
          <Marcador $cor={theme.cores.serie[i]} />
          {series.length > 1 && `${s.nome}:`}
          <strong>{formatarValor(s.pontos[indice]?.valor ?? 0, formato)}</strong>
        </ItemPainel>
      ))}
    </Painel>
  );
}
